(function(window, document) {
  'use strict';

  if (!window) {
    return;
  }

  var ISO_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
  var DMY_PATTERN = /^\d{1,2}-\d{1,2}-\d{4}$/;

  function arrayFrom(value) {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }

  function pad(number) {
    return number < 10 ? '0' + number : String(number);
  }

  function formatISODate(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function toISO(value) {
    if (!value && value !== 0) return null;

    if (typeof value === 'string') {
      var trimmed = value.trim();
      if (!trimmed) return null;
      if (ISO_PATTERN.test(trimmed)) return trimmed;
      if (DMY_PATTERN.test(trimmed)) {
        var parts = trimmed.split('-');
        return parts[2] + '-' + pad(parseInt(parts[1], 10)) + '-' + pad(parseInt(parts[0], 10));
      }
      var parsed = new Date(trimmed);
      if (!isNaN(parsed)) return formatISODate(parsed);
      return null;
    }

    if (value instanceof Date) {
      if (isNaN(value)) return null;
      return formatISODate(value);
    }

    if (typeof value === 'number') {
      var fromNumber = new Date(value);
      if (!isNaN(fromNumber)) return formatISODate(fromNumber);
    }

    if (value && typeof value === 'object') {
      // Common date-range shape { year: yyyy, month: mm, day: dd }
      var year = value.year || value.y || value.Y;
      var month = value.month || value.m;
      var day = value.day || value.d;
      if (year && month && day) {
        return String(year) + '-' + pad(parseInt(month, 10)) + '-' + pad(parseInt(day, 10));
      }
    }

    return null;
  }

  function formatHuman(dateInput, locale) {
    var iso = toISO(dateInput);
    if (!iso) return '';
    var date = new Date(iso + 'T00:00:00');
    if (isNaN(date)) return iso;
    try {
      return date.toLocaleDateString(locale || navigator.language || 'en-US', {
        day: 'numeric',
        month: 'short'
      });
    } catch (err) {
      return iso;
    }
  }

  function expandRange(range) {
    if (!range) return [];
    var startIso = toISO(range.start || range.start_date || range[0]);
    var endIso = toISO(range.end || range.end_date || range[1] || startIso);
    if (!startIso) return [];

    var start = new Date(startIso + 'T00:00:00');
    var end = endIso ? new Date(endIso + 'T00:00:00') : new Date(start);

    if (isNaN(start) || isNaN(end)) return [];

    var results = [];
    var cursor = start;

    while (cursor.getTime() <= end.getTime()) {
      results.push(formatISODate(cursor));
      cursor = new Date(cursor.getTime() + 86400000); // +1 day
    }

    return results;
  }

  function normaliseDisabledList(disabled) {
    if (!disabled) return [];
    if (disabled instanceof Set) return Array.from(disabled);
    if (Array.isArray(disabled)) return disabled.slice();
    return [];
  }

  function buildCalendar(data) {
    data = data || {};

    var disabledSet = new Set();
    var allowedSet = null;

    var allowedCandidates = arrayFrom(data.allowedDates);
    var allowedRangeCandidates = arrayFrom(data.allowedDateRanges);
    if (allowedCandidates.length) {
      allowedSet = new Set();
      allowedCandidates.forEach(function(entry) {
        var iso = toISO(entry);
        if (iso) allowedSet.add(iso);
      });
    }

    if (allowedRangeCandidates.length) {
      if (!allowedSet) {
        allowedSet = new Set();
      }
      allowedRangeCandidates.forEach(function(range) {
        expandRange(range).forEach(function(day) { allowedSet.add(day); });
      });
    }

    var disabledBuckets = [
      data.excludedDates,
      data.blackoutDates,
      data.vendorBlackoutDates,
      data.globalBlackoutDates
    ];

    disabledBuckets.forEach(function(bucket) {
      arrayFrom(bucket).forEach(function(entry) {
        if (!entry && entry !== 0) return;
        if (typeof entry === 'object' && !(entry instanceof Date) && !Array.isArray(entry) && (entry.start || entry.start_date)) {
          expandRange(entry).forEach(function(day) { disabledSet.add(day); });
        } else {
          var iso = toISO(entry);
          if (iso) disabledSet.add(iso);
        }
      });
    });

    var rangeBuckets = [
      data.blackoutRanges,
      data.vendorBlackoutPeriods,
      data.verticalBlackoutRanges,  // Vertical policy holiday_ranges
      data.globalBlackoutRanges,
      data.excludedDateRanges
    ];

    rangeBuckets.forEach(function(bucket) {
      arrayFrom(bucket).forEach(function(range) {
        expandRange(range).forEach(function(day) { disabledSet.add(day); });
      });
    });

    var effectiveAllowed = null;
    if (allowedSet && allowedSet.size) {
      effectiveAllowed = [];
      allowedSet.forEach(function(date) {
        if (!disabledSet.has(date)) {
          effectiveAllowed.push(date);
        }
      });
      effectiveAllowed.sort();
    }

    return {
      disabled: disabledSet,
      allowed: allowedSet,
      effectiveAllowed: effectiveAllowed
    };
  }

  function sortIsoDates(dates) {
    return arrayFrom(dates)
      .map(toISO)
      .filter(Boolean)
      .sort();
  }

  function toDateFromISO(iso) {
    if (!iso) return null;
    var date = new Date(iso + 'T00:00:00');
    return isNaN(date) ? null : date;
  }

  function buildIsoRanges(dates) {
    var sorted = sortIsoDates(dates);
    if (!sorted.length) return [];

    var ranges = [];
    var rangeStart = sorted[0];
    var previous = sorted[0];

    for (var i = 1; i < sorted.length; i += 1) {
      var current = sorted[i];
      var prevDate = toDateFromISO(previous);
      var currDate = toDateFromISO(current);

      if (prevDate && currDate) {
        var diff = currDate.getTime() - prevDate.getTime();
        if (diff > 86400000) {
          ranges.push({ start: rangeStart, end: previous });
          rangeStart = current;
        }
      }

      previous = current;
    }

    ranges.push({ start: rangeStart, end: previous });
    return ranges;
  }

  function formatRange(range, locale) {
    if (!range || !range.start) return '';
    var startText = formatHuman(range.start, locale);
    if (!range.end || range.end === range.start) {
      return startText;
    }
    var endText = formatHuman(range.end, locale);
    return startText + ' - ' + endText;
  }

  function formatRanges(dates, locale) {
    var ranges = buildIsoRanges(dates);
    if (!ranges.length) return '';
    return ranges.map(function(range) {
      return formatRange(range, locale);
    }).join(', ');
  }

  function formatList(dates, locale) {
    return formatRanges(dates, locale);
  }

  function defaultOnlyText(dates, locale) {
    return 'Available only ' + formatRanges(dates, locale);
  }

  function defaultExceptText(dates, locale) {
    return 'Available except ' + formatRanges(dates, locale);
  }

  function summarize(calendar, options) {
    options = options || {};
    var locale = options.locale;
    var disabledList = normaliseDisabledList(calendar.disabled).map(function(date) { return toISO(date); }).filter(Boolean);
    disabledList.sort();

    var enabledList = null;
    if (Array.isArray(calendar.effectiveAllowed)) {
      enabledList = calendar.effectiveAllowed.slice();
    } else if (calendar.effectiveAllowed instanceof Set) {
      enabledList = Array.from(calendar.effectiveAllowed);
    }
    if (enabledList) {
      enabledList = enabledList.map(function(date) { return toISO(date); }).filter(Boolean).sort();
    }

    var status = 'green';
    var text = options.defaultText || 'Available';

    if (enabledList && enabledList.length === 0) {
      status = 'hidden';
      text = options.noneText || 'Not available';
    } else if (enabledList && enabledList.length > 0) {
      status = options.onlyStatus || 'yellow';
      text = options.onlyText ? options.onlyText(enabledList, locale) : defaultOnlyText(enabledList, locale);
    } else if (disabledList.length > 0) {
      status = options.exceptStatus || 'yellow';
      text = options.exceptText ? options.exceptText(disabledList, locale) : defaultExceptText(disabledList, locale);
    }

    return {
      status: status,
      text: text,
      enabledDates: enabledList,
      disabledDates: disabledList
    };
  }

  function intersectSets(a, b) {
    var result = new Set();
    if (!(a instanceof Set)) a = new Set(arrayFrom(a));
    if (!(b instanceof Set)) b = new Set(arrayFrom(b));
    a.forEach(function(value) {
      if (b.has(value)) {
        result.add(value);
      }
    });
    return result;
  }

  function computeProductAvailability(data, options) {
    var calendar = buildCalendar(data);
    var summary = summarize(calendar, options);
    return {
      status: summary.status,
      text: summary.text,
      enabledDates: summary.enabledDates,
      disabledDates: summary.disabledDates,
      calendar: calendar
    };
  }

  /**
   * Expand weeklyClosedDays into individual ISO date strings
   * @param {Array<number>} weeklyClosedDays - Array of day-of-week numbers (0=Sun, 6=Sat)
   * @param {number} monthsAhead - How many months ahead to generate (default 4)
   * @returns {Array<string>} Array of ISO date strings
   */
  function expandWeeklyClosedDays(weeklyClosedDays, monthsAhead) {
    if (!weeklyClosedDays || !weeklyClosedDays.length) return [];

    monthsAhead = monthsAhead || 4;
    var results = [];
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate end date (monthsAhead from today)
    var endDate = new Date(today);
    endDate.setMonth(endDate.getMonth() + monthsAhead);

    var cursor = new Date(today);
    while (cursor <= endDate) {
      if (weeklyClosedDays.indexOf(cursor.getDay()) !== -1) {
        results.push(formatISODate(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    return results;
  }

  function computeCartAvailability(items, options) {
    options = options || {};
    var calendars = [];
    var disabledUnion = new Set();
    var allowedIntersection = null;
    var hasAllowed = false;

    arrayFrom(items).forEach(function(item) {
      if (!item) return;
      var calendar = buildCalendar(item);
      calendars.push({ source: item, calendar: calendar });

      calendar.disabled.forEach(function(date) {
        disabledUnion.add(date);
      });

      // [RELEASE B] Array.isArray alone is the discriminator - do NOT also test
      // .length. buildCalendar returns an array ONLY for an item that actually has
      // an allowed-dates whitelist (null otherwise), so an EMPTY array means "this
      // product has a whitelist and its own blackouts erased every date", not "no
      // whitelist". Testing .length discarded the whitelist entirely and let
      // ordinary, never-whitelisted dates through.
      if (Array.isArray(calendar.effectiveAllowed)) {
        hasAllowed = true;
        var allowedSet = new Set(calendar.effectiveAllowed);
        if (allowedIntersection === null) {
          allowedIntersection = allowedSet;
        } else {
          allowedIntersection = intersectSets(allowedIntersection, allowedSet);
        }
      }
    });

    // [DODOFRESH] Process weeklyClosedDays from options (e.g., [0] for Sunday)
    // Only add weekly closed days to disabled set when NO whitelist exists.
    // When whitelist exists, it's more restrictive and supersedes weekly closures.
    var weeklyClosedDays = options.weeklyClosedDays || [];
    if (!hasAllowed && weeklyClosedDays.length) {
      var closedDates = expandWeeklyClosedDays(weeklyClosedDays, 4);
      closedDates.forEach(function(date) {
        disabledUnion.add(date);
      });
    }

    var enabledFromIntersection = null;
    if (hasAllowed && allowedIntersection) {
      // [DODOFRESH] When whitelist exists, only filter by explicit blackouts (not weekly closures)
      enabledFromIntersection = [];
      allowedIntersection.forEach(function(date) {
        if (!disabledUnion.has(date)) {
          enabledFromIntersection.push(date);
        }
      });
      enabledFromIntersection.sort();
    }

    var summary = summarize({
      disabled: disabledUnion,
      effectiveAllowed: enabledFromIntersection
    }, options);

    return {
      status: summary.status,
      text: summary.text,
      enabledDates: summary.enabledDates,
      disabledDates: summary.disabledDates,
      calendars: calendars,
      weeklyClosedDays: weeklyClosedDays
    };
  }

  function parseJSONScript(id) {
    if (!id || !document) return null;
    var node = document.getElementById(id);
    if (!node) return null;
    try {
      var raw = node.textContent || node.innerHTML || '';
      if (!raw || !raw.trim()) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.warn('[DDMAvailability] Failed to parse JSON for', id, error);
      return null;
    }
  }

  /**
   * Check if a date falls on a weekly closed day
   * @param {Date|string} date - Date object or ISO string (YYYY-MM-DD)
   * @param {Array<number>} weeklyClosedDays - Array of day-of-week numbers (0=Sun, 6=Sat)
   * @returns {boolean} True if date is on a closed day
   */
  function isWeeklyClosedDay(date, weeklyClosedDays) {
    if (!weeklyClosedDays || !weeklyClosedDays.length) return false;
    var d;
    if (typeof date === 'string') {
      d = new Date(date + 'T00:00:00');
    } else if (date instanceof Date) {
      d = date;
    } else {
      return false;
    }
    if (isNaN(d.getTime())) return false;
    return weeklyClosedDays.indexOf(d.getDay()) !== -1;
  }

  /**
   * [DODOFRESH] Format weekly closed days as human-readable text
   * @param {Array<number>} days - Array of day-of-week numbers (0=Sun, 6=Sat)
   * @param {string} [locale] - Locale code (e.g., 'fr', 'en'). Auto-detects from <html lang> if omitted.
   * @returns {string} Human text: "Sundays", "Weekends", "Dimanches", etc., or ""
   */
  function formatWeeklyClosedDays(days, locale) {
    if (!days || !days.length) return '';
    var lang = locale || (document && document.documentElement ? document.documentElement.lang : '') || 'en';
    var isFr = lang.indexOf('fr') === 0;
    var sorted = days.slice().sort();
    // Special case: Weekends
    if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) {
      return isFr ? 'Fins de semaine' : 'Weekends';
    }
    var namesEn = { 0: 'Sundays', 1: 'Mondays', 2: 'Tuesdays', 3: 'Wednesdays', 4: 'Thursdays', 5: 'Fridays', 6: 'Saturdays' };
    var namesFr = { 0: 'Dimanches', 1: 'Lundis', 2: 'Mardis', 3: 'Mercredis', 4: 'Jeudis', 5: 'Vendredis', 6: 'Samedis' };
    var names = isFr ? namesFr : namesEn;
    var parts = [];
    for (var i = 0; i < sorted.length; i++) {
      if (names[sorted[i]]) parts.push(names[sorted[i]]);
    }
    if (parts.length <= 1) return parts[0] || '';
    var joiner = isFr ? ' et ' : ' & ';
    return parts.slice(0, -1).join(', ') + joiner + parts[parts.length - 1];
  }

  /**
   * [DODOFRESH] Union per-item weeklyClosedDays arrays into a single deduplicated array
   * @param {Array<Object>} items - Cart items with optional weeklyClosedDays field
   * @returns {Array<number>} Deduplicated union of all items' closed days
   */
  function unionItemWeeklyClosedDays(items) {
    var seen = {};
    var result = [];
    arrayFrom(items).forEach(function(item) {
      arrayFrom(item && item.weeklyClosedDays).forEach(function(day) {
        if (typeof day === 'number' && !seen[day]) {
          seen[day] = true;
          result.push(day);
        }
      });
    });
    return result.sort();
  }

  /**
   * [RELEASE B] Shift a Date into the store timezone and return its parts.
   * Mirrors pre-checkout DateUtils.getGMT4Today / checkSameDayAvailable.
   */
  function shiftToStoreTz(now, tzOffsetHours) {
    var offsetMinutes = now.getTimezoneOffset() + (tzOffsetHours * 60);
    return new Date(now.getTime() + (offsetMinutes * 60 * 1000));
  }

  function addDaysISO(iso, days) {
    var date = toDateFromISO(iso);
    if (!date) return iso;
    date.setDate(date.getDate() + days);
    return formatISODate(date);
  }

  /**
   * [RELEASE B] Why same-day is unavailable right now, or null if it IS available.
   * Both gates in one place: the policy flag and the clock cutoff, read from the
   * SAME payload snapshot so the two can never be evaluated against different data.
   * @returns {string|null} 'same_day_disabled' | 'cutoff_passed' | null
   */
  function sameDayBlockedReason(payload, shiftedNow) {
    var config = (payload && payload.sameDayConfig) || {};
    if (config.enabled === false) return 'same_day_disabled';
    var match = /^(\d{1,2}):(\d{2})$/.exec(config.cutoff || '');
    if (match) {
      var cutoffMinutes = (+match[1]) * 60 + (+match[2]);
      var currentMinutes = shiftedNow.getHours() * 60 + shiftedNow.getMinutes();
      if (currentMinutes >= cutoffMinutes) return 'cutoff_passed';
    }
    return null;
  }

  /**
   * [RELEASE B] Is same-day delivery available right now for this cart?
   * Exported so pre-checkout's CalendarModule.checkSameDayAvailable() and this
   * module's validateCartDeliveryDate() cannot drift: previously one read the flag
   * live from the payload while the other used a cutoff snapshotted into config at
   * init, so a payload refresh mid-session made them disagree.
   */
  function isSameDayAvailable(payload, now, options) {
    options = options || {};
    var tz = typeof options.tzOffsetHours === 'number' ? options.tzOffsetHours : 4;
    var hasTime = now && typeof now.getTime === 'function' && !isNaN(now.getTime());
    return sameDayBlockedReason(payload, shiftToStoreTz(hasTime ? now : new Date(), tz)) === null;
  }

  /**
   * [RELEASE B] Shared cart delivery-date validation - the single choke point for
   * "is this stored date still valid for this cart". Consumed by BOTH smart-cart.js
   * (drawer) and pre-checkout-v2.js so the two surfaces cannot drift apart.
   *
   * Wraps computeCartAvailability and layers the three things it does not know about:
   * maxLeadHours, sameDayConfig.cutoff, and sameDayConfig.enabled. Check order is
   * identical to the pre-checkout R-42 sequence.
   *
   * NOTE: a product allowed-dates whitelist SUPERSEDES weekly closures - that is
   * computeCartAvailability's documented behaviour, preserved here deliberately.
   *
   * @param {Object} payload - Parsed pcv2-availability-config JSON
   * @param {string|Date} date - Candidate date (DD-MM-YYYY, ISO, or Date)
   * @param {Date} [now] - Current time; injectable so callers/tests can pin it
   * @param {Object} [options] - { tzOffsetHours } (default 4 = Mauritius)
   * @returns {{valid: boolean, reason: string|null, dateISO: string|null, earliestISO: string|null}}
   */
  function validateCartDeliveryDate(payload, date, now, options) {
    options = options || {};
    var tzOffsetHours = typeof options.tzOffsetHours === 'number' ? options.tzOffsetHours : 4;
    // Duck-typed, not `instanceof Date`: a Date from another realm (iframe) fails
    // the instanceof check and would silently fall back to the wall clock.
    var hasTime = now && typeof now.getTime === 'function' && !isNaN(now.getTime());
    now = hasTime ? now : new Date();

    function reject(reason, dateISO, earliestISO) {
      return { valid: false, reason: reason, dateISO: dateISO || null, earliestISO: earliestISO || null };
    }

    var dateISO = toISO(date);
    if (!dateISO) return reject('date_invalid_generic', null);

    payload = payload || {};
    var sameDayConfig = payload.sameDayConfig || {};

    var shiftedNow = shiftToStoreTz(now, tzOffsetHours);
    var todayISO = formatISODate(shiftedNow);

    // Check 1: date in the past
    if (dateISO < todayISO) return reject('date_passed', dateISO);

    // Check 2: same-day - gated on BOTH the policy flag and the clock cutoff
    var sameDayBlocked = sameDayBlockedReason(payload, shiftedNow);
    if (dateISO === todayISO && sameDayBlocked) {
      return reject(sameDayBlocked, dateISO);
    }

    // The per-item union (vertical U vendor, per item) is authoritative; the
    // top-level weeklyClosedDays is dominant-vertical-only. Mirrors exactly what
    // CalendarModule.computeDisabledDates feeds computeCartAvailability - do NOT
    // fall back to the top-level array when the union is empty, or this helper
    // would apply closed days pre-checkout does not.
    var items = arrayFrom(payload.items);
    var weeklyClosedDays = items.length
      ? unionItemWeeklyClosedDays(items)
      : arrayFrom(payload.weeklyClosedDays);

    var availability = computeCartAvailability(items, { weeklyClosedDays: weeklyClosedDays });
    var enabledDates = availability.enabledDates;

    // Check 3: whitelist, else weekly closures.
    // A whitelist SUPERSEDES weekly closures, so the two are mutually exclusive.
    // The weekly test runs BEFORE the generic disabled set below on purpose:
    // computeCartAvailability folds closed days into disabledDates, so testing
    // day-of-week first is what lets callers show "we deliver Mon-Fri" instead of
    // a generic blocked-date message. It also catches dates beyond
    // expandWeeklyClosedDays' 4-month horizon, which the disabled set misses.
    if (enabledDates !== null && enabledDates !== undefined) {
      // effectiveAllowed is already blackout-filtered, so membership implies allowed
      if (!enabledDates.length || enabledDates.indexOf(dateISO) === -1) {
        return reject('date_blocked', dateISO);
      }
    } else {
      if (isWeeklyClosedDay(dateISO, weeklyClosedDays)) {
        return reject('weekly_closed', dateISO);
      }
      // Check 4: explicit blackouts (vendor, vertical holiday, product excluded)
      if (availability.disabledDates && availability.disabledDates.indexOf(dateISO) !== -1) {
        return reject('date_blocked', dateISO);
      }
    }

    // Check 5: prep lead time. Must mirror CalendarModule.getMinDate() EXACTLY:
    // start at today, +1 day when same-day is unavailable, THEN + ceil(hours/24).
    // Dropping that +1 lets the drawer keep a date pre-checkout rejects - e.g. a
    // 72h pack at 12:00 (past the 11:30 cutoff): pre-checkout's floor is today+4,
    // so today+3 must fail here too.
    var leadDays = Math.ceil((sameDayConfig.maxLeadHours || 0) / 24);
    var earliestISO = todayISO;
    if (sameDayBlocked) earliestISO = addDaysISO(earliestISO, 1);
    if (leadDays > 0) earliestISO = addDaysISO(earliestISO, leadDays);
    if (dateISO < earliestISO) return reject('lead_time_conflict', dateISO, earliestISO);

    return { valid: true, reason: null, dateISO: dateISO, earliestISO: earliestISO };
  }

  /**
   * [UPSELL S5] The same payload narrowed to a subset of its lines, with the
   * line-derived parts of sameDayConfig recomputed (prep lead time, item-level
   * same-day opt-out). Cutoff and vertical policy stay cart-wide on purpose: a
   * subset is never judged more leniently on those than the cart itself.
   */
  function subsetPayload(payload, items) {
    var config = payload.sameDayConfig || {};
    var maxLeadHours = 0;
    var itemsAllowSameDay = true;
    items.forEach(function(item) {
      var hours = parseInt(item.prepTimeHours, 10) || 0;
      if (hours > maxLeadHours) maxLeadHours = hours;
      if (item.sameDayEnabled === false) itemsAllowSameDay = false;
    });
    // Older payloads have no verticalEnabled: keep the cart-wide flag as it is.
    var enabled = typeof config.verticalEnabled === 'boolean'
      ? (config.verticalEnabled && itemsAllowSameDay)
      : config.enabled;
    return {
      items: items,
      sameDayConfig: { enabled: enabled, cutoff: config.cutoff, maxLeadHours: maxLeadHours },
      weeklyClosedDays: payload.weeklyClosedDays
    };
  }

  /**
   * [UPSELL S5] Which upsell extras (items with a truthy `upsell` flag, i.e. cart
   * lines carrying `_upsell_placement`) are the ONLY reason `date` is undeliverable?
   *
   * Returns [] unless the full cart rejects the date AND the cart without its extras
   * accepts it; then returns each extra that, next to the main lines, rejects it.
   * An extra is therefore never blamed for a date the main items block anyway.
   *
   * @param {Object} payload - Parsed pcv2-availability-config JSON
   * @param {string|Date} date - Candidate date (DD-MM-YYYY, ISO, or Date)
   * @param {Date} [now] - Current time; injectable for tests
   * @param {Object} [options] - { tzOffsetHours } (default 4 = Mauritius)
   * @returns {Array<Object>} blocking extra items (payload item objects)
   */
  function findBlockingExtras(payload, date, now, options) {
    payload = payload || {};
    var items = arrayFrom(payload.items).filter(Boolean);
    var extras = items.filter(function(item) { return !!item.upsell; });
    var main = items.filter(function(item) { return !item.upsell; });
    if (!extras.length || !main.length) return [];
    if (validateCartDeliveryDate(payload, date, now, options).valid) return [];
    if (!validateCartDeliveryDate(subsetPayload(payload, main), date, now, options).valid) return [];
    return extras.filter(function(extra) {
      return !validateCartDeliveryDate(subsetPayload(payload, main.concat([extra])), date, now, options).valid;
    });
  }

  window.DDMAvailability = {
    toISO: toISO,
    formatHuman: formatHuman,
    formatList: formatList,
    formatRanges: formatRanges,
    buildCalendar: buildCalendar,
    summarize: summarize,
    computeProductAvailability: computeProductAvailability,
    computeCartAvailability: computeCartAvailability,
    parseJSONScript: parseJSONScript,
    expandRange: expandRange,
    expandWeeklyClosedDays: expandWeeklyClosedDays,
    isWeeklyClosedDay: isWeeklyClosedDay,
    formatWeeklyClosedDays: formatWeeklyClosedDays,
    unionItemWeeklyClosedDays: unionItemWeeklyClosedDays,
    isSameDayAvailable: isSameDayAvailable,
    validateCartDeliveryDate: validateCartDeliveryDate,
    findBlockingExtras: findBlockingExtras,
    helpers: {
      intersectSets: intersectSets,
      arrayFrom: arrayFrom
    }
  };

  if (document && typeof document.dispatchEvent === 'function') {
    try {
      document.dispatchEvent(new CustomEvent('ddm:availability:ready', {
        detail: { version: '1.0.0' }
      }));
    } catch (err) {
      try {
        var readyEvent = document.createEvent('Event');
        readyEvent.initEvent('ddm:availability:ready', true, true);
        document.dispatchEvent(readyEvent);
      } catch (e) {
        // noop
      }
    }
  }

})(typeof window !== 'undefined' ? window : undefined, typeof document !== 'undefined' ? document : undefined);
